import Vue from 'vue';
import Vuex from 'vuex';
import axios from 'axios';
// eslint-disable-next-line import/no-cycle
import authStore from './modules/authentication';
import { isNonProdEnv, profileSectionURLs } from '../config/oidc';

import {
  INTERNET_NUMBER_RESOURCE_TYPE,
  RESOURCE_LIST_STATUS,
  validateInternetNumberType,
  normalizeInternetNumber,
} from '../utils/validateInternetNumberResource';

axios.defaults.timeout = 2000; // 2 sec

Vue.use(Vuex);

export default new Vuex.Store({
  state: {
    showSideBar: true,
    searchResult: null,
    searchResultList: null,
    errorMsg: '',
    loading: false,
    networkInfo: null,
    query: '', // what user searched
    comparisonResourceList: [],
    comparisonToolList: [],
    comparisonToolCategory: window.innerWidth < 768 ? '' : 'All',
    comparisonToolQuery: '',
    selectedComparisonToolList: [],
    isNarrowWidthDevice:
      window.innerWidth < 768 ||
      window.orientation === 90 ||
      window.orientation === -90 ||
      (screen && screen.orientation && screen.orientation.angle === 90) ||
      (screen && screen.orientation && screen.orientation.angle === -90),
    isComparisonParamsChanged: false,
    isExpandSidebar: true,
    isShowSidebarOnMobile: false,
    isLargeWidthDevice: true,
    isExtraLargeWidthDevice: true,
    themeDataList: [],
  },
  getters: {
    tabs: (state) => {
      if (!state.searchResult) {
        return [];
      }
      return state.searchResult.map((i) => {
        return { id: i.id, layout: i.layout, name: i.name };
      });
    },
    showSideBar: (state) => {
      return state.showSideBar;
    },
    currentWidgets: (state) => (tabId) => {
      if (tabId === 'apnic-comparison') {
        if (
          !state.selectedComparisonToolList.length ||
          !state.comparisonResourceList.length
        )
          return [];

        let selectedWidgetList = [];

        // Flatten the searchResultList
        state.searchResultList.forEach((searchResult) => {
          searchResult.tabs.forEach((tab) => {
            tab.widgets.forEach((widget) => {
              selectedWidgetList.push(widget);
            });
          });
        });

        // Set widgets in array according to widgets type and remove duplicated widgets
        let newWidgetList = [];

        state.selectedComparisonToolList.forEach((selectedComparisonTool) => {
          const widgetsListWithSameType = [];
          selectedWidgetList.forEach((widget) => {
            if (widget.id.indexOf(selectedComparisonTool.id) > -1) {
              if (widgetsListWithSameType.length === 0) {
                widgetsListWithSameType.push(widget);
                newWidgetList.push(widgetsListWithSameType);
              } else {
                if (
                  widgetsListWithSameType.every(
                    (i) =>
                      i.widgetObject.resourceAddedByUser !==
                      widget.widgetObject.resourceAddedByUser
                  )
                )
                  widgetsListWithSameType.push(widget);
              }
            }
          });

          // If there is no widget available for this tool, set an empty array as one element of newWidgetList
          if (widgetsListWithSameType.length === 0)
            newWidgetList.push(widgetsListWithSameType);
        });

        // Check each tool to confirm whether it is available for every element in the added Internet number resource list. If the tool is not available for an Internet number, fill up the newWidgetList with a specific Object
        newWidgetList.forEach((widgets) => {
          if (widgets.length === state.comparisonResourceList.length) return;

          if (widgets.length === 0) return;

          for (
            let index = 0;
            index < state.comparisonResourceList.length;
            index++
          ) {
            if (
              widgets[index] &&
              widgets[index].widgetObject &&
              widgets[index].widgetObject.resourceAddedByUser &&
              widgets[index].widgetObject.resourceAddedByUser ===
                state.comparisonResourceList[index]
            ) {
              continue;
            } else {
              widgets.splice(index, 0, {
                id: widgets[0].id,
                type: widgets[0].type,
                widgetObject: {
                  selector: state.comparisonResourceList[index],
                  resourceType: validateInternetNumberType(
                    state.comparisonResourceList[index]
                  ),
                },
              });
            }
          }
        });

        return newWidgetList;
      } else {
        if (!state.searchResult || !tabId) {
          return [];
        }
        const result = state.searchResult.find((i) => i.id === tabId);
        return result.widgets;
      }
    },
    query: (state) => {
      return state.query;
    },
    loading: (state) => {
      return state.loading;
    },
    networkInfo: (state) => {
      return state.networkInfo;
    },
    resultExist: (state) => {
      return state.errorMsg || state.searchResult;
    },
    resultListExist: (state) => {
      return state.errorMsg || state.searchResultList;
    },
    errorMsg: (state) => {
      return state.errorMsg;
    },
    isComparisonSubmitDisabled: (state) => {
      const isResourceAndToolSelected =
        state.comparisonResourceList.length &&
        state.selectedComparisonToolList.length;

      return !(state.isComparisonParamsChanged && isResourceAndToolSelected);
    },
    comparisonResourceListStatus: (state) => {
      // Set comparisonResourceListStatus according to the Internet number resources added, i.e. whether they are only prefix or only AS or mix
      if (state.comparisonResourceList.length === 0)
        return RESOURCE_LIST_STATUS.EMPTY;

      const isContainingASNumber = state.comparisonResourceList.some(
        (resource) => {
          const validateResult = validateInternetNumberType(resource);
          if (validateResult === INTERNET_NUMBER_RESOURCE_TYPE.AS_NUMBER)
            return true;

          return false;
        }
      );

      if (!isContainingASNumber) return RESOURCE_LIST_STATUS.PREFIX_ONLY;

      const isAllASNumber = state.comparisonResourceList.every((resource) => {
        const validateResult = validateInternetNumberType(resource);
        if (validateResult === INTERNET_NUMBER_RESOURCE_TYPE.AS_NUMBER)
          return true;

        return false;
      });

      if (isAllASNumber) return RESOURCE_LIST_STATUS.AS_ONLY;

      return RESOURCE_LIST_STATUS.PREFIX_AND_AS;
    },
  },
  mutations: {
    setShowSideBar: (state, bool) => {
      state.showSideBar = bool;
    },
    setErrorMsg: (state, msg) => {
      state.errorMsg = msg;
    },
    setQuery: (state, query) => {
      state.query = query;
    },
    setSearchResult: (state, searchResult) => {
      state.searchResult = searchResult;
    },
    setSearchResultList: (state, searchResultList) => {
      state.searchResultList = searchResultList;
    },
    preloadWidgets: (state) => {
      const tabs = state.searchResult;
      tabs.forEach((tab) => {
        const widgets = tab.widgets;
        widgets.forEach((widget) => {
          const widgetId = `${tab.id}_${widget.id}`;
          widget.widgetObject = {
            widgetName: widget.type,
            params: { resource: state.query },
            selector: widgetId,
            styling: {
              // 'logo', 'container', 'feedback', 'maximize'
              disable: ['logo', 'feedback', 'maximize'],
            },
          };
        });
      });
    },
    preloadWidgetsOfSearchResultList: (state) => {
      state.searchResultList.forEach((searchResult, index) => {
        searchResult.tabs.forEach((tab) => {
          tab.widgets.forEach((widget) => {
            widget.widgetObject = {
              widgetName: widget.type,
              params: { resource: searchResult.resource },
              selector: searchResult.resource.startsWith('AS')
                ? `${searchResult.resource}_${tab.id}_${widget.id}`
                : `prefix_${index}_${tab.id}_${widget.id}`,
              styling: {
                disable: ['logo', 'feedback', 'maximize'],
              },
              resourceAddedByUser: state.comparisonResourceList[index],
            };
          });
        });
      });
    },
    filterWidgetsByComparisonToolList: (state) => {
      state.searchResultList.forEach((comparisonSearchResult) => {
        const isASResource = comparisonSearchResult.resource.startsWith('AS');

        comparisonSearchResult.tabs.forEach((tab) => {
          const widgets = tab.widgets.filter((widget) => {
            let isSelectedWidget = false;

            state.selectedComparisonToolList.forEach((selectedTool) => {
              const tool = selectedTool.id.startsWith('-')
                ? isASResource
                  ? 'as' + selectedTool.id
                  : 'prefix' + selectedTool.id
                : selectedTool.id;

              if (widget.id.indexOf(tool) > -1) {
                isSelectedWidget = true;
              }
            });
            return isSelectedWidget;
          });

          tab.widgets = widgets;
        });
      });
    },
    setLoadingState: (state, loading) => {
      state.loading = loading;
    },
    setNetworkInfo: (state, info) => {
      state.networkInfo = info;
    },
    clearSearch: (state) => {
      state.searchResult = null;
      state.searchResultList = null;
      state.errorMsg = '';
    },
    addComparisonResourceIntoList: (state, val) => {
      state.comparisonResourceList.push(val);
      state.isComparisonParamsChanged = true;
      state.searchResultList = null;
    },
    setComparisonResourceList: (state, resourceList) => {
      state.comparisonResourceList = resourceList;
      state.isComparisonParamsChanged = true;
      state.searchResultList = null;
    },
    removeFromComparisonResourceList: (state, index) => {
      state.comparisonResourceList.splice(index, 1);
      state.isComparisonParamsChanged = true;

      // set searchResultList as null to remove the whole comparison result
      // state.searchResultList = null;

      // delete the corresponding resource from searchResultList to update the comparison result rather than remove the whole comparison result
      state.searchResultList.splice(index, 1);
    },
    updateComparisonToolCategory: (state, toolCategory) => {
      state.comparisonToolCategory = toolCategory;
    },
    updateComparisonToolList: (state, toolList) => {
      state.comparisonToolList = toolList;
    },
    updateComparisonToolQuery: (state, toolQuery) => {
      state.comparisonToolQuery = toolQuery;
    },
    updateSelectedComparisonToolList: (state, toolList) => {
      state.selectedComparisonToolList = toolList;
      state.isComparisonParamsChanged = true;
      state.searchResultList = null;
    },
    removeFromComparisonToolList: (state, removedTool) => {
      const updatedToolList = state.selectedComparisonToolList.filter(
        (item) => item.name !== removedTool.name
      );
      state.selectedComparisonToolList = updatedToolList;
      state.isComparisonParamsChanged = true;
    },
    setIsNarrowWidthDevice: (state, isNarrowWidthDevice) => {
      state.isNarrowWidthDevice = isNarrowWidthDevice;
    },
    setIsComparisonParamsChanged: (state, val) => {
      state.isComparisonParamsChanged = val;
    },
    setIsExpandSidebar: (state, bool) => {
      state.isExpandSidebar = bool;
    },
    setIsShowSidebarOnMobile: (state, bool) => {
      state.isShowSidebarOnMobile = bool;
    },
    setIsLargeWidthDevice: (state, isLargeWidthDevice) => {
      state.isLargeWidthDevice = isLargeWidthDevice;
    },
    setIsExtraLargeWidthDevice: (state, isExtraLargeWidthDevice) => {
      state.isExtraLargeWidthDevice = isExtraLargeWidthDevice;
    },
    setThemeDataList: (state, data) => {
      state.themeDataList = data;
    },
  },
  actions: {
    toggleSideBar: (context) => {
      context.commit('setShowSideBar', !context.state.showSideBar);
    },
    getNetworkInfo: (context) => {
      const { commit, state } = context;

      if (
        state.networkInfo &&
        state.networkInfo.asn &&
        state.networkInfo.prefix
      )
        return;

      let info = { ip: '8.8.8.8', prefix: '8.8.8.0/24', asn: '15169' };
      commit('setLoadingState', true);
      axios
        .get('https://api64.ipify.org', { timeout: 5000 })
        .then(({ data }) => {
          if (data) {
            let ip = data;
            axios
              .get(
                'https://stat.ripe.net/data/network-info/data.json?resource=' +
                  ip,
                {
                  timeout: 5000,
                }
              )
              .then(({ data }) => {
                if (data.data.prefix && data.data.asns.length > 0) {
                  info = {
                    ip: ip,
                    prefix: data.data.prefix,
                    asn: data.data.asns[0],
                  };
                }
                commit('setNetworkInfo', info);
                commit('setLoadingState', false);
              })
              .catch(() => {
                commit('setNetworkInfo', info);
                commit('setLoadingState', false);
              });
          } else {
            commit('setNetworkInfo', info);
            commit('setLoadingState', false);
          }
        })
        .catch(() => {
          commit('setNetworkInfo', info);
          commit('setLoadingState', false);
        });
    },
    searchResource: ({ commit, dispatch }, resource) => {
      commit('setLoadingState', true);
      commit('clearSearch');

      const normalizedResource = normalizeInternetNumber(resource);
      commit('setQuery', normalizedResource);

      if (!window.ripestat) {
        setTimeout(() => dispatch('searchResource', resource), 500);
        return;
      }

      // Fetch the page structure from our widget host.
      // STAT_WIDGET_API_BASE is defined in src/config/ripe.js
      let url =
        process.env.VUE_APP_STAT_DATA_API_URL +
        'results-page-structure/data.json?resource=' +
        resource;
      // we need to use RIPE for this call for the moment until we can replicate it in netox-api
      // let url = 'https://stat.ripe.net/data/results-page-structure/data.json?profile=apnic&resource=' + resource;

      // not sure what is the profile
      // var profileName = $.deparam.querystring().profile;
      // if (profileName) {
      //   pageStructureURL += '&amp;profile=' + profileName;
      // }
      axios
        .get(url, { timeout: 60 * 1000 })
        .then(({ data }) => {
          commit('setLoadingState', false);
          if (data.data.resource.valid) {
            commit('setSearchResult', data.data.tabs);
            // data.data.resource.canonical and resource seems the same value
            commit('setQuery', data.data.resource.canonical);
            commit('preloadWidgets');
          } else {
            commit(
              'setErrorMsg',
              'Given resource is not valid. Please try a different one!'
            );
          }
        })
        .catch(() => {
          commit('setLoadingState', false);
          commit(
            'setErrorMsg',
            'Loading page structure failed! Please check your Internet connection. If it persists please contact help@apnic.net'
          );
        });
    },
    searchResourceList: ({ commit, dispatch }, searchQuery) => {
      commit('setLoadingState', true);
      commit('clearSearch');

      if (!window.ripestat) {
        setTimeout(() => dispatch('searchResourceList', searchQuery), 500);
        return;
      }

      const resourceListStr = searchQuery.split('_')[0];
      const resourceList = resourceListStr.split(',');
      const requestList = [];

      resourceList.forEach((resource) => {
        const url =
          process.env.VUE_APP_STAT_DATA_API_URL +
          'results-page-structure/data.json?resource=' +
          resource;

        requestList.push(axios.get(url, { timeout: 60 * 1000 }));
      });

      Promise.all(requestList)
        .then((resList) => {
          commit('setLoadingState', false);

          const searchResultList = [];

          resList.forEach((res) => {
            searchResultList.push({
              isResourceValid: res.data.data.resource.valid,
              tabs: res.data.data.tabs,
              resource: res.data.data.resource.canonical,
            });
          });

          commit('setSearchResultList', searchResultList);
          commit('filterWidgetsByComparisonToolList');
          commit('preloadWidgetsOfSearchResultList');
        })
        .catch(() => {
          commit('setLoadingState', false);
          commit(
            'setErrorMsg',
            'Loading page structure failed! Please check your Internet connection. If it persists please contact help@apnic.net'
          );
        });
    },

    fetchThemeDataList: (context) => {
      axios
        .get('https://webdist.data.apnic.net/lists/theme-v1.json')
        .then((res) => {
          const themeDataList = res.data;

          if (isNonProdEnv) {
            // Adjust omnibarCurrentUserAccountLinks and omnibarCurrentUserMembershipLinks to replace prod links by testing ones.
            const omnibarCurrentUserAccountLinksAdjusted =
              themeDataList.omnibarCurrentUserAccountLinks.map((item) => {
                switch (item.label_en_us) {
                  case 'MyAPNIC':
                    item.link = profileSectionURLs.myApnicDashboardURL;
                    break;
                  case 'Personal profile':
                    item.link = profileSectionURLs.personalSettingsURL;
                    break;
                  case 'Passwords & Security':
                    item.link = profileSectionURLs.passwordSecurityURL;
                    break;
                  default:
                    break;
                }
                return item;
              });

            // Remove the item of "Log out" when adjusting omnibarCurrentUserMembershipLinks as it'll be handled by our codebase.
            const omnibarCurrentUserMembershipLinksAdjusted =
              themeDataList.omnibarCurrentUserMembershipLinks
                .filter((item) => item.label_en_us !== 'Log out')
                .map((item) => {
                  switch (item.label_en_us) {
                    case 'Link existing membership':
                      item.link = profileSectionURLs.linkExistingMembership;
                      break;
                    case 'New membership application':
                      item.link = profileSectionURLs.membershipApplication;
                      break;
                    default:
                      break;
                  }
                  return item;
                });

            const themeDataListAdjusted = {
              ...themeDataList,
              omnibarCurrentUserAccountLinks:
                omnibarCurrentUserAccountLinksAdjusted,
              omnibarCurrentUserMembershipLinks:
                omnibarCurrentUserMembershipLinksAdjusted,
            };

            context.commit('setThemeDataList', themeDataListAdjusted);
          } else {
            context.commit('setThemeDataList', themeDataList);
          }
        })
        .catch((err) => {
          console.error(err);
        });
    },
  },
  modules: {
    authStore,
  },
});
